import React, { useEffect, useState } from "react";
import { MdUpload } from "react-icons/md";
import { Icon } from "semantic-ui-react";

import { ImageView } from "../ImageView/ImageView";

import { convertImageToPng, renameFileName } from "../../utils/imageUtils";
import "./imageUploaderNew.scss";

interface NewImageUploaderProps {
  defaultImage?: string | null;
  deleteButtonView?: boolean;
  uploadButtonView?: boolean;
  customImageViewMain?: string;
  customFileView?: string;
  buttonTitle?: string;
  imageData?: (file: File | null) => void;
  deleteImage?: () => void;
  customImageUploadMain?: string;
  mainTitle?: string;
  customImageDeleteMain?: string;
  convertToPng?: boolean; // Enable automatic PNG conversion
  customFileName?: string; // Custom filename for the uploaded image (without extension)
  testId?: string;
}

/**
 * Converts any image file to PNG format using Canvas API
 * @param file - The original image file
 * @param fileName - Optional custom filename (defaults to original name with .png extension)
 * @returns Promise resolving to a PNG File object
 */

export const NewImageUploader = ({
  defaultImage = null,
  deleteButtonView = true,
  uploadButtonView = true,
  customImageViewMain,
  customFileView,
  buttonTitle = "Add Image",
  imageData,
  deleteImage,
  customImageUploadMain,
  mainTitle = "Product Image",
  customImageDeleteMain,
  convertToPng = false,
  customFileName,
  testId
}: NewImageUploaderProps) => {
  const [file, setFile] = useState<any>(defaultImage);
  const [isConverting, setIsConverting] = useState(false);

  useEffect(() => {
    setFile(defaultImage);
  }, [defaultImage]);

  /**
   * Renames a file with a custom name while preserving the extension
   */

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files[0]) {
      let selectedFile = e.target.files[0];

      // Rename file if customFileName is provided
      if (customFileName) {
        selectedFile = renameFileName(selectedFile, customFileName);
      }

      // If PNG conversion is not enabled, use original file
      if (!convertToPng) {
        setFile(URL.createObjectURL(selectedFile));
        imageData?.(selectedFile);
        return;
      }

      // Convert to PNG if enabled
      try {
        setIsConverting(true);

        // Convert image to PNG (with custom name if provided)
        const pngFileName = customFileName ? `${customFileName}.png` : undefined;
        const pngFile = await convertImageToPng(selectedFile, pngFileName);

        // Set preview
        setFile(URL.createObjectURL(pngFile));

        // Pass PNG file to parent
        imageData?.(pngFile);
      } catch (error) {
        // Fallback: use original file if conversion fails
        setFile(URL.createObjectURL(selectedFile));
        imageData?.(selectedFile);
      } finally {
        setIsConverting(false);
      }
    }
  }

  return (
    <div data-testid={testId} className={`imageUploaderMain ${customImageUploadMain || ""}`}>
      <p className="labelUploder">{mainTitle}</p>
      <div className="imageUploaderContent">
        {file ? (
          <div className="imagePreviewContainer">
            <ImageView
              avatar={false}
              src={file}
              addRandomId={false}
              customImageView={`${customImageViewMain || ""} imageViewMainOther`}
              data-testid={testId ? `${testId}-image` : undefined}
            />
            {deleteButtonView ? (
              <button
                type="button"
                className={`deleteButtonContentMain ${customImageDeleteMain || ""}`}
                onClick={() => {
                  setFile(null);
                  imageData?.(null);
                  deleteImage?.();
                }}
                aria-label="Delete image"
              >
                <Icon name={"trash alternate"} size={"large"} />
              </button>
            ) : null}
          </div>
        ) : null}
        {uploadButtonView ? (
          <label
            className={`${customFileView || ""} custom-file-upload ${
              isConverting ? "converting" : ""
            }`}
          >
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleFileChange}
              disabled={isConverting}
              aria-label="Upload image"
            />
            <MdUpload size={20} className="uploadIcon" />
            <span className="uploadText">
              {isConverting ? "Converting to PNG..." : buttonTitle}
            </span>
          </label>
        ) : null}
      </div>
    </div>
  );
};
